import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Cpu, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import ZentraLogo from "@/components/ZentraLogo";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const roleHome = (role) => {
        if (role === "client") return "/portal";
        if (role === "cleaner" || role === "technician") return "/operario";
        return "/";
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        setLoading(true);
        try {
            const u = await login(email, password);
            navigate(roleHome(u?.role), { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-zentra-bg" data-testid="login-page">
            {/* Left: brand panel */}
            <aside className="hidden lg:flex w-1/2 zentra-login-side relative overflow-hidden flex-col p-12">
                <div className="absolute inset-0 zentra-dot-bg opacity-40" />
                <div className="relative z-10 flex flex-col h-full">
                    <ZentraLogo variant="light" size="md" layout="stacked" />
                    <div className="mt-auto">
                        <h1
                            className="font-display font-bold text-white tracking-tight"
                            style={{ fontSize: "44px", lineHeight: "1.05", letterSpacing: "-0.02em" }}
                        >
                            El centro de
                            <br />
                            <span className="text-zentra-accent">operaciones</span>
                            <br />
                            de tu negocio.
                        </h1>
                        <p className="mt-6 text-white/70 text-base max-w-md leading-relaxed">
                            Limpieza, mantenimiento, incidencias, almacén y apartamentos turísticos. Todo bajo una
                            sola consola operativa.
                        </p>

                        <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
                            <FeaturePill icon={ShieldCheck} label="Control total" />
                            <FeaturePill icon={Cpu} label="Tecnología" />
                            <FeaturePill icon={Sparkles} label="Operativa" />
                        </div>
                    </div>
                    <div className="mt-10 text-[11px] tracking-widest uppercase text-white/40">
                        © {new Date().getFullYear()} ZENTRA · by Valmay Group
                    </div>
                </div>
            </aside>

            {/* Right: form */}
            <main className="flex-1 flex items-center justify-center p-6 md:p-12 relative" data-testid="login-form-side">
                <div className="absolute top-6 left-6 lg:hidden">
                    <ZentraLogo variant="dark" layout="horizontal" size="sm" />
                </div>
                <div className="w-full max-w-md">
                    <div className="mb-8">
                        <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong mb-3">
                            Acceso seguro
                        </p>
                        <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight">
                            Bienvenido al centro de control.
                        </h2>
                        <p className="text-sm text-zentra-muted mt-2">
                            Inicia sesión con tus credenciales de operación.
                        </p>
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="zentra-label" htmlFor="email">
                                Usuario o email
                            </label>
                            <input
                                id="email"
                                type="text"
                                required
                                className="zentra-input"
                                placeholder="superadmin"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <label className="zentra-label" htmlFor="password">
                                Contraseña
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                className="zentra-input"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                autoComplete="current-password"
                            />
                        </div>

                        {error && (
                            <div
                                className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm"
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="zentra-btn-accent w-full justify-center"
                            data-testid="login-submit-button"
                        >
                            {loading ? "Verificando…" : "Entrar"}
                            {!loading && <ArrowRight size={16} />}
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
}

function FeaturePill({ icon: Icon, label }) {
    return (
        <div className="flex flex-col items-start gap-2 p-3 rounded-md border border-white/10 bg-white/5">
            <div className="w-7 h-7 rounded-md bg-zentra-accent/20 flex items-center justify-center text-zentra-accent">
                <Icon size={14} />
            </div>
            <span className="text-xs font-medium text-white/80 tracking-tight">{label}</span>
        </div>
    );
}
